import React from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import { useProjectStore } from '../../store/projectStore';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiUsers, FiBriefcase, FiClock, FiStar } = FiIcons;

const Analytics = () => {
  const { projects, prospects } = useProjectStore();

  // Calculate stats
  const totalProjects = projects.length;
  const totalProspects = prospects.length;
  const avgScore = prospects.length > 0 ? (prospects.reduce((sum, p) => sum + p.aiScore, 0) / prospects.length).toFixed(1) : 0;
  const completionRate = totalProjects > 0 ? ((prospects.length / (totalProjects * 10)) * 100).toFixed(1) : 0;

  // Chart options
  const prospectsByStatusOption = {
    title: {
      text: 'Prospectos por Estado',
      left: 'center',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold'
      }
    },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)'
    },
    legend: {
      orient: 'vertical',
      left: 'left'
    },
    series: [
      {
        name: 'Estado',
        type: 'pie',
        radius: '50%',
        data: [
          { value: prospects.filter(p => p.status === 'new').length, name: 'Nuevos' },
          { value: prospects.filter(p => p.status === 'reviewing').length, name: 'En Revisión' },
          { value: prospects.filter(p => p.status === 'preselected').length, name: 'Preseleccionados' },
          { value: prospects.filter(p => p.status === 'rejected').length, name: 'Rechazados' }
        ],
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0,0,0,0.5)'
          }
        }
      }
    ]
  };

  const scoreDistributionOption = {
    title: {
      text: 'Distribución de Puntuaciones IA',
      left: 'center',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      }
    },
    xAxis: {
      type: 'category',
      data: ['1 Estrella', '2 Estrellas', '3 Estrellas', '4 Estrellas', '5 Estrellas']
    },
    yAxis: {
      type: 'value'
    },
    series: [
      {
        name: 'Candidatos',
        type: 'bar',
        data: [
          prospects.filter(p => p.aiScore === 1).length,
          prospects.filter(p => p.aiScore === 2).length,
          prospects.filter(p => p.aiScore === 3).length,
          prospects.filter(p => p.aiScore === 4).length,
          prospects.filter(p => p.aiScore === 5).length
        ],
        itemStyle: {
          color: '#015ea7'
        }
      }
    ]
  };

  const monthlyTrendOption = {
    title: {
      text: 'Tendencia Mensual de Entrevistas',
      left: 'center',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold'
      }
    },
    tooltip: {
      trigger: 'axis'
    },
    xAxis: {
      type: 'category',
      data: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun']
    },
    yAxis: {
      type: 'value'
    },
    series: [
      {
        name: 'Entrevistas',
        type: 'line',
        data: [12, 19, 15, 25, 32, 28],
        smooth: true,
        itemStyle: {
          color: '#015ea7'
        },
        areaStyle: {
          color: 'rgba(1,94,167,0.1)'
        }
      }
    ]
  };

  const stats = [
    {
      title: 'Total Proyectos',
      value: totalProjects,
      icon: FiBriefcase,
      color: 'bg-blue-500',
      trend: '+12%'
    },
    {
      title: 'Total Prospectos',
      value: totalProspects,
      icon: FiUsers,
      color: 'bg-green-500',
      trend: '+23%'
    },
    {
      title: 'Puntuación Promedio',
      value: `${avgScore}/5`,
      icon: FiStar,
      color: 'bg-yellow-500',
      trend: '+0.3'
    },
    {
      title: 'Tasa de Completado',
      value: `${completionRate}%`,
      icon: FiTrendingUp,
      color: 'bg-purple-500',
      trend: '+5%'
    }
  ];

  return (
    <div className="p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Análisis y Métricas</h1>
        <p className="text-gray-600">Insights detallados sobre tu proceso de selección</p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                <SafeIcon icon={stat.icon} className="w-6 h-6 text-white" />
              </div>
              <span className="text-green-600 text-sm font-medium">{stat.trend}</span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
            <p className="text-gray-600 text-sm">{stat.title}</p>
          </motion.div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <ReactECharts option={prospectsByStatusOption} style={{ height: '300px' }} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <ReactECharts option={scoreDistributionOption} style={{ height: '300px' }} />
        </motion.div>
      </div>

      {/* Monthly Trend */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white rounded-xl shadow-lg p-6 mb-8"
      >
        <ReactECharts option={monthlyTrendOption} style={{ height: '400px' }} />
      </motion.div>

      {/* Top Performing Projects */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <h3 className="text-xl font-bold text-gray-900 mb-6">Proyectos con Mejor Rendimiento</h3>
        {projects.length > 0 ? (
          <div className="space-y-4">
            {projects.slice(0, 5).map((project, index) => {
              const projectProspects = prospects.filter(p => p.projectId === project.id);
              const avgProjectScore = projectProspects.length > 0 ? 
                (projectProspects.reduce((sum, p) => sum + p.aiScore, 0) / projectProspects.length).toFixed(1) : 0;

              return (
                <div key={project.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold">#{index + 1}</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{project.title}</h4>
                      <p className="text-sm text-gray-600">{project.company}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center space-x-1 mb-1">
                      {Array.from({ length: 5 }, (_, i) => (
                        <SafeIcon
                          key={i}
                          icon={FiStar}
                          className={`w-4 h-4 ${
                            i < Math.floor(avgProjectScore) ? 'text-yellow-400 fill-current' : 'text-gray-300'
                          }`}
                        />
                      ))}
                      <span className="ml-2 text-sm text-gray-600">({avgProjectScore})</span>
                    </div>
                    <p className="text-sm text-gray-500">{projectProspects.length} candidatos</p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <SafeIcon icon={FiBriefcase} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No hay datos de proyectos disponibles</p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Analytics;